import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { channels } from '@/mocks/channels';

export default function YouTubeSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const featured = channels.slice(0, 6);

  return (
    <section ref={ref} className="section-padding bg-[#080808] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-[#d4af37]/20 to-transparent"></div>
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-80 h-80 rounded-full bg-[#d4af37]/3 blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#d4af37]"></span>
            <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">Digital Network</span>
            <span className="w-8 h-px bg-[#d4af37]"></span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Our YouTube <span className="text-[#d4af37]">Empire</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            23+ channels, 100M+ subscribers, 100B+ views — the largest Indian Hindi Devotional and folk music network on YouTube.
          </p>
          <div className="flex items-center justify-center gap-8 mt-8">
            <div className="text-center">
              <div className="text-3xl font-bold font-heading text-[#d4af37]">23+</div>
              <div className="text-white/60 text-sm">Channels</div>
            </div>
            <div className="w-px h-10 bg-[#d4af37]/20"></div>
            <div className="text-center">
              <div className="text-3xl font-bold font-heading text-[#d4af37]">100M+</div>
              <div className="text-white/60 text-sm">Subscribers</div>
            </div>
            <div className="w-px h-10 bg-[#d4af37]/20"></div>
            <div className="text-center">
              <div className="text-3xl font-bold font-heading text-[#d4af37]">100B+</div>
              <div className="text-white/60 text-sm">Total Views</div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {featured.map((ch, i) => (
            <Link
              key={ch.id}
              to="/youtube-network"
              className={`group glass-card rounded-2xl overflow-hidden cursor-pointer transition-all duration-700 hover:border-[#d4af37]/40 hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="h-40 overflow-hidden relative">
                <img
                  alt={`${ch.name} YouTube channel`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  src={ch.thumbnail}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-red-600">
                  <i className="ri-youtube-fill text-white text-sm"></i>
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-heading text-base font-bold text-white mb-1 group-hover:text-[#d4af37] transition-colors">{ch.name}</h3>
                <div className="flex items-center gap-2 text-white/50 text-xs">
                  <i className="ri-user-line text-[#d4af37]"></i>
                  <span>{ch.subscribers}</span>
                  <span className="mx-1">•</span>
                  <i className="ri-eye-line text-[#d4af37]"></i>
                  <span>{ch.category}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className={`text-center transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Link to="/youtube-network" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold text-black whitespace-nowrap btn-gold cursor-pointer transition-all hover:scale-105">
            Explore All Channels <i className="ri-youtube-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
